/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useEffect, useId, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useUserId } from '@/api/hooks';
import {
  getSchematicProperties,
  getUserSettings,
  saveSchematicProperties,
  saveUserSettings,
  type HomePreference,
  type ThemeSetting,
} from '@/api/settings';
import { Alert, Button, cn, SegmentedControl, Slider, Switch, toast } from '@/design-system';
import { useUiStore, type ThemePreference } from '@/stores/uiStore';
import { ShellDialog } from './ShellDialog';

interface Draft {
  symbolSize: number;
  wireSize: number;
  spacing: number;
  showEngineeringLabels: boolean;
  showCommonViewTypes: boolean;
}

const normalizePreference = (value?: string): HomePreference =>
  value === 'manufacturer' ? 'manufacturer' : 'dealer';

const DEFAULT_DRAFT: Draft = {
  symbolSize: 35,
  wireSize: 3,
  spacing: 30,
  showEngineeringLabels: false,
  showCommonViewTypes: true,
};

/**
 * One "label | control" row. Below 481px the control drops under its label; a switch row
 * (`inline`) keeps the two side by side, because a switch is narrow and reads best beside its label.
 */
function Row({
  label,
  inline = false,
  children,
}: {
  label: string;
  inline?: boolean;
  children: (labelId: string) => ReactNode;
}) {
  const id = useId();
  return (
    <div className={cn('set-row', inline && 'is-inline')}>
      <span className="set-label" id={id}>
        {label}
      </span>
      <div className="set-ctl">{children(id)}</div>
    </div>
  );
}

export function SettingsModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const gmin = useUserId();
  const queryClient = useQueryClient();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const themes: { value: ThemePreference; label: string }[] = [
    { value: 'system', label: t('settings.themeSystem') },
    { value: 'light', label: t('settings.themeLight') },
    { value: 'dark', label: t('settings.themeDark') },
  ];

  const enabled = Boolean(gmin);
  const settingsQuery = useQuery({
    queryKey: ['user-settings', gmin],
    queryFn: () => getUserSettings(gmin),
    enabled,
    retry: false,
  });
  const schematicQuery = useQuery({
    queryKey: ['schematic-properties', gmin],
    queryFn: () => getSchematicProperties(gmin),
    enabled,
    retry: false,
  });

  const [draft, setDraft] = useState<Draft>(DEFAULT_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Only sync the schematic-display draft from the backend. The theme is intentionally NOT
    // re-applied from settingsQuery here: it is already restored on startup by useSyncUserSettings,
    // and re-applying cached data on open clobbered the user's in-session choice (e.g. reverting an
    // active dark theme to the saved light value). The Appearance control still edits the live theme.
    const p = schematicQuery.data;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft((prev) => ({
      symbolSize: p?.symbolSize ?? prev.symbolSize,
      wireSize: p?.wireSize ?? prev.wireSize,
      spacing: p?.spacing ?? prev.spacing,
      showEngineeringLabels: p?.showEngineeringLabels ?? prev.showEngineeringLabels,
      showCommonViewTypes: p?.showCommonViewTypes ?? prev.showCommonViewTypes,
    }));
  }, [schematicQuery.data]);

  const set =
    <K extends keyof Draft>(key: K) =>
    (value: Draft[K]) =>
      setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    if (!gmin) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Plant, language and home preference are managed elsewhere (language lives in the header);
      // preserve the user's saved values so persisting theme/appearance never clears them.
      const current = settingsQuery.data;
      await saveUserSettings(gmin, {
        plant: current?.plant?.plantCode ?? 'AR',
        language: current?.languageDisplay?.languageCode ?? 'en',
        preference: normalizePreference(current?.preferenceCode),
        theme: theme as ThemeSetting,
      });
      await saveSchematicProperties(gmin, {
        userId: gmin,
        symbolSize: draft.symbolSize,
        wireSize: draft.wireSize,
        spacing: draft.spacing,
        showEngineeringLabels: draft.showEngineeringLabels,
        // Schematic wire colors are not defined yet, so there is no preference to offer or keep.
        showWireColors: false,
        showCommonViewTypes: draft.showCommonViewTypes,
      });
      void queryClient.invalidateQueries({ queryKey: ['user-settings', gmin] });
      void queryClient.invalidateQueries({ queryKey: ['schematic-properties', gmin] });
      toast.success(t('toast.settingsSaved'));
      onClose();
    } catch {
      setError(t('settings.error'));
    } finally {
      setSaving(false);
    }
  };

  const slider = (key: 'symbolSize' | 'wireSize' | 'spacing', min: number, max: number) =>
    function SliderControl(labelId: string) {
      return (
        <Slider
          value={draft[key]}
          min={min}
          max={max}
          showValue
          aria-labelledby={labelId}
          onValueChange={set(key)}
          wrapperClassName="set-slider"
        />
      );
    };

  return (
    <ShellDialog
      onClose={onClose}
      icon="settings"
      title={t('settings.title')}
      description={t('settings.subtitle')}
      busy={saving}
      className="set-modal"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            {t('settings.cancel')}
          </Button>
          <Button type="button" onClick={save} disabled={saving}>
            {saving ? t('settings.saving') : t('settings.save')}
          </Button>
        </>
      }
    >
      <h3 className="set-sec">{t('settings.schematicDisplay')}</h3>
      <div className="set-list">
        <Row label={t('settings.symbolSize')}>{slider('symbolSize', 25, 50)}</Row>
        <Row label={t('settings.wireSize')}>{slider('wireSize', 1, 7)}</Row>
        <Row label={t('settings.spacing')}>{slider('spacing', 20, 60)}</Row>
        <Row label={t('settings.displayCode')}>
          {() => (
            <SegmentedControl
              aria-label={t('settings.displayCode')}
              value={draft.showEngineeringLabels ? 'engineering' : 'service'}
              options={[
                { value: 'service', label: t('settings.serviceCode') },
                { value: 'engineering', label: t('settings.engineeringCode') },
              ]}
              onValueChange={(v) => set('showEngineeringLabels')(v === 'engineering')}
              className="set-seg"
            />
          )}
        </Row>
        <Row label={t('settings.highlightCommon')} inline>
          {(labelId) => (
            <Switch
              checked={draft.showCommonViewTypes}
              onCheckedChange={set('showCommonViewTypes')}
              aria-labelledby={labelId}
              className="set-switch"
            />
          )}
        </Row>
      </div>

      <div className="set-divider" role="presentation" />
      <h3 className="set-sec">{t('settings.preferences')}</h3>
      <div className="set-list">
        <Row label={t('settings.appearance')}>
          {() => (
            <SegmentedControl
              aria-label={t('settings.appearance')}
              value={theme}
              options={themes}
              onValueChange={setTheme}
              className="set-seg"
            />
          )}
        </Row>
      </div>

      {error && (
        <Alert variant="danger" className="set-error">
          {error}
        </Alert>
      )}
    </ShellDialog>
  );
}
